import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { computePackageDetails, toEnginePackage } from '@/lib/reconcile';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PackageCard } from './PackageCard';
import type { FormulaWarning } from './ChargeReviewBanner';
import { QuoteTotalMismatchBanner } from './QuoteTotalMismatchBanner';
import { AddOnSection } from './AddOnSection';
import { ChargeRow } from './ChargeRow';
import type {
  CreateFullQuoteInput,
  ReplaceFullQuoteInput,
  LineItemInput,
  ChargeInput,
  AddOnInput,
} from '@everplan/shared';

type LineItemData = Omit<LineItemInput, 'sortOrder'>;
type ChargeData = Omit<ChargeInput, 'sortOrder'>;
type AddOnData = Omit<AddOnInput, 'sortOrder'>;

interface PackageData {
  name: string;
  description?: string | null;
  isSelected?: boolean;
  lineItems: LineItemData[];
  additionalCharges: ChargeData[];
  statedTotal?: number | null;
}

interface EditorFormData {
  name: string;
  packages: PackageData[];
  addOns: AddOnData[];
  additionalCharges: ChargeData[];
}

interface EditorFormProps {
  initialData?: EditorFormData;
  sourceDocumentUrl?: string | null;
  sourceDocumentMimeType?: string | null;
  onSave: (data: CreateFullQuoteInput | ReplaceFullQuoteInput) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  isSaving?: boolean;
  saveLabel?: string;
  headerContent?: React.ReactNode;
  extractionWarnings?: string[];
  extractionConfidence?: 'high' | 'medium' | 'low';
  formulaWarnings?: FormulaWarning[];
}

function createEmptyPackage(name = 'Standard'): PackageData {
  return {
    name,
    description: null,
    isSelected: false,
    lineItems: [],
    additionalCharges: [],
    statedTotal: null,
  };
}

function buildSavePayload(
  form: EditorFormData,
  sourceDocumentUrl?: string | null,
  sourceDocumentMimeType?: string | null
): Omit<CreateFullQuoteInput, 'itemId' | 'status'> {
  return {
    name: form.name,
    sourceDocumentUrl: sourceDocumentUrl ?? null,
    sourceDocumentMimeType: sourceDocumentMimeType ?? null,
    packages: form.packages.map((pkg, pkgIdx) => ({
      name: pkg.name,
      description: pkg.description ?? null,
      isSelected: pkg.isSelected ?? false,
      sortOrder: pkgIdx,
      lineItems: pkg.lineItems.map((item, itemIdx) => ({
        ...item,
        sortOrder: itemIdx,
      })),
      additionalCharges: pkg.additionalCharges.map((charge, chargeIdx) => ({
        ...charge,
        sortOrder: chargeIdx,
      })),
    })),
    addOns: form.addOns.map((addOn, idx) => ({
      ...addOn,
      sortOrder: idx,
    })),
    additionalCharges: form.additionalCharges.map((charge, idx) => ({
      ...charge,
      sortOrder: idx,
    })),
  };
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export function EditorForm({
  initialData,
  sourceDocumentUrl,
  sourceDocumentMimeType,
  onSave,
  onDirtyChange,
  isSaving = false,
  saveLabel,
  headerContent,
  extractionWarnings,
  extractionConfidence,
  formulaWarnings,
}: EditorFormProps) {
  const [form, setForm] = useState<EditorFormData>(() => {
    if (initialData) {
      return {
        name: initialData.name,
        packages: initialData.packages.length > 0
          ? initialData.packages
          : [createEmptyPackage()],
        addOns: initialData.addOns,
        additionalCharges: initialData.additionalCharges ?? [],
      };
    }
    return {
      name: '',
      packages: [createEmptyPackage()],
      addOns: [],
      additionalCharges: [],
    };
  });

  const initialSnapshotRef = useRef<EditorFormData | null>(null);
  if (initialSnapshotRef.current === null) {
    initialSnapshotRef.current = JSON.parse(JSON.stringify(form));
  }
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialSnapshotRef.current);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const selectedPkgs = form.packages.filter((pkg) => pkg.isSelected);
  const hasSelection = selectedPkgs.length > 0;

  const addOnsTotal = form.addOns
    .filter((addOn) => addOn.quantity != null && addOn.quantity > 0)
    .reduce((sum, addOn) => sum + addOn.quantity! * addOn.unitCost, 0);

  const quoteChargeTotal = form.additionalCharges.reduce((sum, ch) => sum + ch.amount, 0);
  const base = addOnsTotal + quoteChargeTotal;

  // Live recompute: delegate to the shared calc engine so percentage charges
  // reflect edits to qty/unitCost (and, eventually, sibling charges). For
  // flat-only packages this matches the previous hand-rolled math exactly.
  // NOTE: `selectedAddOns` is intentionally `[]` here — the editor doesn't yet
  // model per-package add-on selection; tracked for Phase 5.
  const packageTotals = form.packages.map(
    (pkg) => computePackageDetails(toEnginePackage(pkg)).packageTotal
  );
  const showRange = !hasSelection && form.packages.length > 1;
  const grandTotalMin = Math.min(...packageTotals) + base;
  const grandTotalMax = Math.max(...packageTotals) + base;
  const grandTotal = hasSelection
    ? form.packages.reduce(
        (sum, pkg, idx) => (pkg.isSelected ? sum + (packageTotals[idx] ?? 0) : sum),
        0,
      ) + base
    : packageTotals.reduce((sum, t) => sum + t, 0) + base;

  const updatePackage = (index: number, updated: PackageData) => {
    const newPackages = [...form.packages];
    newPackages[index] = updated;
    setForm({ ...form, packages: newPackages });
  };

  const removePackage = (index: number) => {
    if (form.packages.length <= 1) return; // Keep at least one package
    setForm({
      ...form,
      packages: form.packages.filter((_, i) => i !== index),
    });
  };

  const addPackage = () => {
    setForm({
      ...form,
      packages: [...form.packages, createEmptyPackage(`Package ${form.packages.length + 1}`)],
    });
  };

  const handleSave = () => {
    const payload = buildSavePayload(form, sourceDocumentUrl, sourceDocumentMimeType);
    onSave(payload as CreateFullQuoteInput | ReplaceFullQuoteInput);
    initialSnapshotRef.current = JSON.parse(JSON.stringify(form));
  };

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable form content */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 p-4">
          {headerContent}
          <QuoteTotalMismatchBanner warnings={formulaWarnings ?? []} />
          {(extractionWarnings?.length || (extractionConfidence && extractionConfidence !== 'high')) && (
            <Alert variant={extractionConfidence === 'low' ? 'destructive' : 'default'}>
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {extractionConfidence === 'low' ? 'Low-confidence extraction' : 'Review extracted items'}
              </AlertTitle>
              <AlertDescription>
                {extractionConfidence && extractionConfidence !== 'high' && (
                  <p>Extraction confidence: {extractionConfidence}.</p>
                )}
                {extractionWarnings && extractionWarnings.length > 0 && (
                  <ul className="mt-1 list-disc pl-4 text-xs">
                    {extractionWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
          {/* Quote Name */}
          <Input
            id="name"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            placeholder="Quote name — e.g. 2024 Rate Sheet"
            className="text-base font-semibold"
          />

          {/* Packages */}
          <div className="space-y-3">
            {form.packages.map((pkg, i) => (
              <PackageCard
                key={i}
                pkg={pkg}
                onChange={(updated) => updatePackage(i, updated)}
                onRemove={() => removePackage(i)}
                showSelection={form.packages.length > 1}
                warnings={(formulaWarnings ?? []).filter(
                  (w) => w.scope === 'package' && w.packageIndex === i,
                )}
              />
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addPackage}
              className="w-full"
            >
              <Plus className="size-4" />
              Add Package
            </Button>
          </div>

          {/* Add-Ons */}
          <AddOnSection
            addOns={form.addOns}
            onChange={(addOns) => setForm({ ...form, addOns })}
          />

          {/* Quote-level required fees */}
          <div className="rounded-lg border border-border bg-card p-3 shadow-ambient">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold">Required Fees</h3>
              <span className="text-xs text-muted-foreground">
                Charges that always apply regardless of package
              </span>
            </div>
            <div className="mt-2 space-y-0.5">
              {form.additionalCharges.map((charge, i) => (
                <ChargeRow
                  key={i}
                  charge={charge}
                  onChange={(updated) => {
                    const next = [...form.additionalCharges];
                    next[i] = updated;
                    setForm({ ...form, additionalCharges: next });
                  }}
                  onRemove={() => {
                    setForm({
                      ...form,
                      additionalCharges: form.additionalCharges.filter((_, idx) => idx !== i),
                    });
                  }}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() =>
                  setForm({
                    ...form,
                    additionalCharges: [
                      ...form.additionalCharges,
                      { type: 'other' as const, description: null, amount: 0 },
                    ],
                  })
                }
                className="mt-0.5 text-muted-foreground"
              >
                <Plus className="size-3" />
                Add Fee
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="shrink-0 border-t border-border bg-card px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-foreground">
              {showRange ? 'Estimated Range' : 'Grand Total'}
            </span>
            <p className="text-lg font-bold">
              {showRange && grandTotalMin !== grandTotalMax
                ? `${formatDollars(grandTotalMin)} – ${formatDollars(grandTotalMax)}`
                : formatDollars(showRange ? grandTotalMin : grandTotal)}
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving || !form.name.trim()}
            className="min-w-[120px]"
          >
            {isSaving ? 'Saving...' : (saveLabel ?? 'Save Quote')}
          </Button>
        </div>
      </div>
    </div>
  );
}
